import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTreasuryClient } from "../lib/client";
import { useProfiles } from "../lib/profiles";
import { ButtonCancel, ButtonOk } from "./Buttons";
import { Editable, EditableDecimalString, Enumerable, Labelled } from ".";

export function BankingInfoEditable({ profile = null, isEditingInitial, ...props }) {
  let queryClient = useQueryClient();
  let { active } = useProfiles();

  let navigate = useNavigate()
  let treasury = useTreasuryClient();
  let { mutate: createProfile } = useMutation({
    mutationFn: async function createSellerProfileFn({ profile }) {
      setIsEditing(false)
      return await treasury.createProfile(profile)
    },
    onSuccess: async function createProfileSuccessFn(data) {
        await queryClient.invalidateQueries({queryKey: ["GET", `/profile/${active}`]})
        setIsEditing(false);
      navigate(0);
    },
    onError: function(e) {
      console.error(e)
    }
  })

  let { mutate: updateProfile } = useMutation({
    mutationFn: async function updateSellerProfileFn({ profile }) {
      setIsEditing(false)
      return await treasury.updateProfile(profile)
    },
      onSuccess: function updateProfileSuccessFn(data) {
          setIsEditing(false);
      navigate(0);
    }
  });

  function getBankingInfoInput(formData) {
    let bankingInfo = {
      accountHolderName: formData.get("accountHolderName"),
      financialInstitutionName: formData.get("financialInstitutionName"),
      country: formData.get("country"),
      accountType: formData.get("accountType"),
      accountNumber: formData.get("accountNumber"),
    };

    if (bankingInfo.country == "USA") {
      bankingInfo.address = {
        street: formData.get("addressStreet"),
        city: formData.get("addressCity"),
        state: formData.get("addressState"),
        zipCode: formData.get("addressZipCode")
      }
      bankingInfo.routingNumber = formData.get("routingNumber");
    } else {
      bankingInfo.transitNumber = formData.get("transitNumber");
      bankingInfo.institutionNumber = formData.get("institutionNumber");
    }

    return bankingInfo;
  }

  function submitCreateProfile(e) {
    e.preventDefault();
    let bankingInfo = getBankingInfoInput(new FormData(e.target));
    createProfile({ profile: { bankingInfo } })
  }

  function submitUpdateProfile(e) {
    e.preventDefault();
    let bankingInfo = getBankingInfoInput(new FormData(e.target));
    updateProfile({ profile: { bankingInfo } })
  }

    console.log("isEI", isEditingInitial);
  let [isEditing, setIsEditing] = useState(isEditingInitial);

  let [isCountryUSA, setIsCountryUSA] = useState(true)
  useEffect(() => {
    if (profile)  {
      setIsCountryUSA(profile?.bankingInfo?.country == "USA")
    } else {
      setIsCountryUSA(true)
    }
  }, [profile]);


    return <div className="w-full max-w-1/2">
        <Labelled label="" className="">
        <div hidden={!profile && !isEditing}>
        <div class="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 mb-10" role="alert" hidden={!!profile}>
        <p class="font-bold">Direct Deposit Banking Profile</p>
        <p class="text-sm">Enter the particulars of the account you wish to conduct direct deposits to.  The information you require is printed in machine-readable digits along the bottom of cheques for the account.  We presently support deposit into either USA or Canadian bank accounts.</p>
        </div>


    <form onSubmit={!profile ? submitCreateProfile : submitUpdateProfile} className="space-y-12 sm:space-y-16">
  {/* Hidden inputs or other form metadata if needed */}
  
  <div>
    <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">Bank Account Information</h2>
    <p className="mt-1 max-w-2xl text-sm/6 text-gray-600 dark:text-gray-400">
	{profile ? "Review or modify your configured banking information." : "Enter your banking details to enable deposits and withdrawals."}
    </p>

    <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:border-t-gray-900/10 sm:pb-0 dark:border-white/10 dark:sm:divide-white/10 dark:sm:border-t-white/10">
      
      {/* Account Holder Name */}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
        <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
          Account holder name
        </label>
        <div className="mt-2 sm:col-span-2 sm:mt-0">
          <Editable 
            isEditing={isEditing} 
            name="accountHolderName" 
            required
            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-md sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
          >
            {profile?.bankingInfo?.accountHolderName}
          </Editable>
        </div>
      </div>

      {/* Financial Institution Name */}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
        <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
          Financial institution name
        </label>
        <div className="mt-2 sm:col-span-2 sm:mt-0">
          <Editable 
            isEditing={isEditing} 
            name="financialInstitutionName" 
            required
            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-md sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
          >
            {profile?.bankingInfo?.financialInstitutionName}
          </Editable>
        </div>
      </div>

      {/* Country Selection */}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
        <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
          Country
        </label>
        <div className="mt-2 sm:col-span-2 sm:mt-0">
          <div className="grid grid-cols-1 sm:max-w-xs">
            <Enumerable 
              isEditing={isEditing} 
              name="country" 
              options={["Canada", "USA"]} 
              defaultValue="USA" 
              value={isCountryUSA && "USA" || "Canada"} 
              required
              onChange={(e) => setIsCountryUSA(e.target.value == "USA")}
              className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:focus:outline-indigo-500"
            >
              {profile?.bankingInfo?.country}
            </Enumerable>
            <svg viewBox="0 0 16 16" fill="currentColor" className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4 dark:text-gray-400">
              <path d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" fillRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      {/* Address Section - USA Only */}
      {isCountryUSA && (
        <>
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
              Street address
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
              <Editable 
                isEditing={isEditing} 
                name="addressStreet" 
                required={isCountryUSA}
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-xl sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              >
                {profile?.bankingInfo?.address?.street}
              </Editable>
            </div>
          </div>

          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
              City
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
              <Editable 
                isEditing={isEditing} 
                name="addressCity" 
                required={isCountryUSA}
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-xs sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              >
                {profile?.bankingInfo?.address?.city}
              </Editable>
            </div>
          </div>

          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
              State / ZIP code
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 grid grid-cols-2 gap-4 sm:max-w-md">
              <Editable 
                isEditing={isEditing} 
                name="addressState" 
                required={isCountryUSA}
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              >
                {profile?.bankingInfo?.address?.state}
              </Editable>
              <Editable 
                isEditing={isEditing} 
                name="addressZipCode" 
                required={isCountryUSA}
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              >
                {profile?.bankingInfo?.address?.zipCode}
              </Editable>
            </div>
          </div>
        </>
      )}

      {/* Account Type */}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
        <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
          Account type
        </label>
        <div className="mt-2 sm:col-span-2 sm:mt-0">
          <div className="grid grid-cols-1 sm:max-w-xs">
            <Enumerable 
              isEditing={isEditing} 
              name="accountType" 
              options={["Chequing", "Savings"]} 

              defaultValue={profile?.bankingInfo?.accountType} 
              required
              className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:focus:outline-indigo-500"
            >
              {profile?.bankingInfo?.accountType}
            </Enumerable>
            <svg viewBox="0 0 16 16" fill="currentColor" className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4 dark:text-gray-400">
              <path d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" fillRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      {/* Cheque Image & Alert */}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
        <div className="sm:pt-1.5"></div>
        <div className="mt-2 sm:col-span-2 sm:mt-0 space-y-4">
          {isCountryUSA ? 
            <img className="w-full max-w-md rounded-lg" src="/img/cheque-us.png" alt="US Cheque template" /> :
            <img className="w-full max-w-md rounded-lg" src="/img/cheque-ca.png" alt="Canadian Cheque template" />
          }
          <div className="flex items-center rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
            <svg className="h-5 w-5 flex-shrink-0 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18.278 9.5a.75.75 0 0 0-1.45-.583 7.5 7.5 0 0 1-2.64 9.974.75.75 0 0 0 .584 1.166h.001c4.323 0 8.097-2.741 9.533-6.714a.75.75 0 0 0-.863-.891zM6.722 1.5a.75.75 0 0 1 .416 1.242 7.5 7.5 0 0 1 2.655 9.966.75.75 0 0 1-1.242-.416 9 9 0 0 0-3.167-6.482.75.75 0 0 1 .826-1.31z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-blue-800 dark:text-blue-200">
              Review the numbers below to ensure they align with the cheque template above.
            </p>
          </div>
        </div>
      </div>

      {/* Routing/Transit Numbers - Conditional */}
      {isCountryUSA && (
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
          <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
            Routing number
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0 w-20">
            <EditableDecimalString 
              isEditing={isEditing} 
              name="routingNumber" 
              required={isCountryUSA} 
              maxLength={9} 
              minLength={9}
              className="block rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-sm sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
            >
              {profile?.bankingInfo?.routingNumber}
            </EditableDecimalString>
          </div>
        </div>
      )}

      {!isCountryUSA && (
        <>
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
              Transit number
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 w-20">
              <Editable 
                isEditing={isEditing} 
                name="transitNumber" 
                pattern="^[0-9]{5,6}$" 
                required={!isCountryUSA}
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-sm sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              >
                {profile?.bankingInfo?.transitNumber}
              </Editable>
            </div>
          </div>

          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
            <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
              Institution number
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 w-20">
              <Editable 
                isEditing={isEditing} 
                name="institutionNumber" 
                pattern="^[0-9]{3}$" 
                required={!isCountryUSA}
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-sm sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              >
                {profile?.bankingInfo?.institutionNumber}
              </Editable>
            </div>
          </div>
        </>
      )}

      {/* Account Number */}
      <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
        <label className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5 dark:text-white">
          Account number
        </label>
        <div className="mt-2 sm:col-span-2 sm:mt-0 w-40">
          <Editable 
            isEditing={isEditing} 
            name="accountNumber" 
            pattern="^[0-9]+$"
            required
            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:max-w-md sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
          >
            {profile?.bankingInfo?.accountNumber}
          </Editable>
        </div>
      </div>
    </div>
  </div>

  {/* Action Buttons */}
  {isEditing && (
    <div className="mt-6 flex items-center justify-end gap-x-6">
      {!!profile && (
        <ButtonCancel 
          type="button"
          onClick={() => {
            setIsEditing(false);
            setIsCountryUSA(profile?.bankingInfo?.country == "USA");
          }}
          className="text-sm/6 font-semibold text-gray-900 dark:text-white"
        >
          Cancel
        </ButtonCancel>
      )}
      <ButtonOk 
        type="submit"
        className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
      >
        {!profile ? "Create Profile" : "Save Updated Profile"}
      </ButtonOk>
    </div>
  )}
</form>
    

	 
    </div>

        {!isEditing && <div className="flex flex-row gap-4 w-full justify-between">
              <ButtonCancel onClick={_ => {
                  navigate("/sales")
               }}>
                Home
              </ButtonCancel>
        <ButtonOk hidden={isEditing} className="w-fit" onClick={() => setIsEditing(true)}>Edit Banking Profile</ButtonOk>
        </div> }
  </Labelled>
    </div>
}
